import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { OverlayQueue, OverlayType, RegisterOverlaysArgs } from "@swiggy-private/cart-types";
import { CROUTON_CLOSE_DELAY, OVERLAY_DELAYS } from "@checkout-core/constants/overlay";
import { getHighestElement } from "@checkout-core/helpers/overlay";

const QUEUE_INIT_STATE = {
    [OverlayType.MODAL]: [],
    [OverlayType.HALFCARD]: [],
    [OverlayType.LOTTIE]: [],
    [OverlayType.SAVINGS_ANIMATION]: [],
    [OverlayType.CROUTON]: [],
};

export const useRegisterModalAndHalfcard = (
    setEnabledOverlayId: React.Dispatch<React.SetStateAction<{ enabledOverlayId: string }>>,
) => {
    const [finalPriorityQueue, setFinalPriorityQueue] = useState<RegisterOverlaysArgs[]>([]);

    const overlayQueue = useRef<OverlayQueue>(QUEUE_INIT_STATE);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const updateFinalQueue = useCallback(() => {
        const highestPriorityModal = getHighestElement(overlayQueue.current[OverlayType.MODAL]);
        const highestPriorityHalfcard = getHighestElement(overlayQueue.current[OverlayType.HALFCARD]);
        const highestPriorityLottie = getHighestElement(overlayQueue.current[OverlayType.LOTTIE]);
        const highestPrioritySavingsAnimation = getHighestElement(overlayQueue.current[OverlayType.SAVINGS_ANIMATION]);

        /* Here lowest number will have higher priority
        ex: bolt_popup: 1, m2c_halfcard: 2, lottie: MAX_INT
        in this case elements will be ordered by priority with lottie always last
         */
        const finalQueue = [
            highestPriorityModal,
            highestPriorityHalfcard,
            highestPriorityLottie,
            highestPrioritySavingsAnimation,
        ]
            .filter(Boolean)
            .sort((a, b) => (a?.priority ?? 0) - (b?.priority ?? 0));

        setFinalPriorityQueue(finalQueue as RegisterOverlaysArgs[]);

        overlayQueue.current = {
            [OverlayType.MODAL]: [],
            [OverlayType.HALFCARD]: [],
            [OverlayType.LOTTIE]: [],
            [OverlayType.SAVINGS_ANIMATION]: [],
            [OverlayType.CROUTON]: [],
        };
    }, []);

    const registerOverlays = useCallback(
        (args: RegisterOverlaysArgs) => {
            const { id, overlayType } = args;
            const currentQueue = overlayQueue.current[overlayType];

            if (!currentQueue.some((crouton) => crouton.id === id)) currentQueue.push(args);

            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                updateFinalQueue();
            }, OVERLAY_DELAYS.HALFCARD_AND_MODAL);

            return () => {
                if (timerRef.current) clearTimeout(timerRef.current);
            };
        },
        [updateFinalQueue],
    );

    const onCloseOverlay = useCallback(() => {
        setTimeout(() => setFinalPriorityQueue((queue) => queue.slice(1)), CROUTON_CLOSE_DELAY);
    }, []);

    const onCloseOverlayImmediate = useCallback(() => {
        setFinalPriorityQueue((queue) => queue.slice(1));
    }, []);
    const overlayProps = useMemo(() => {
        const enabledOverlayId = finalPriorityQueue[0]?.id || "";
        const enabledOverlayType = finalPriorityQueue[0]?.overlayType || null;
        return {
            registerOverlays,
            enabledOverlayId,
            enabledOverlayType,
            onCloseOverlay,
            onCloseOverlayImmediate,
        };
    }, [finalPriorityQueue, onCloseOverlay, onCloseOverlayImmediate, registerOverlays]);

    useEffect(() => {
        setEnabledOverlayId({
            enabledOverlayId: finalPriorityQueue[0]?.id || "",
        });
    }, [finalPriorityQueue, setEnabledOverlayId]);

    return overlayProps;
};
